import { useCallback, useEffect, useRef } from 'react';

import { ExternalLinkIcon, UploadIcon } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

import { cn } from '@/lib/utils';

import Link from '../Link/Link';

type QrCodeSize = 'sm' | 'md' | 'lg';

interface QrCodeGeneratorProps {
  url: string;
  image: string;
  size?: QrCodeSize;
  width?: number; // Legacy: use size prop instead
  height?: number; // Legacy: use size prop instead
  margin?: number; // Legacy: use size prop instead
  overlayType?: 'download' | 'link';
  className?: string;
}

const QR_CODE_SIZES = {
  sm: { size: 80, className: 'w-20 h-20' }, // 80px
  md: { size: 96, className: 'w-24 h-24' }, // 96px
  lg: { size: 112, className: 'w-28 h-28' }, // 112px
} as const;

const QrCodeGenerator = ({
  url,
  image,
  size = 'md',
  width: legacyWidth,
  height: legacyHeight,
  margin: legacyMargin,
  overlayType = 'download',
  className,
}: QrCodeGeneratorProps) => {
  const sizeConfig = QR_CODE_SIZES[size];

  // Legacy support: allow width/height/margin override
  const qrSize = legacyWidth ?? legacyHeight ?? sizeConfig.size;
  const qrMargin = legacyMargin ?? 0;

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeInstanceRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!qrCodeInstanceRef.current) {
      qrCodeInstanceRef.current = new QRCodeStyling({
        width: qrSize,
        height: qrSize,
        margin: qrMargin,
        imageOptions: { margin: 2, imageSize: 0.5 },
      });
      if (qrCodeRef.current) {
        qrCodeInstanceRef.current.append(qrCodeRef.current);
      }
    }
  }, [qrSize, qrMargin]);

  useEffect(() => {
    if (url && qrCodeInstanceRef.current) {
      qrCodeInstanceRef.current.update({ data: url, image });
    }
  }, [url, image]);

  const handleClick = useCallback(
    () => qrCodeInstanceRef.current?.download({ name: `qr-${Date.now()}`, extension: 'png' }),
    []
  );

  const overlayClasses =
    'absolute inset-0 flex items-center justify-center opacity-0 transition-opacity hover:opacity-100';

  const usesLegacySize = legacyWidth != null || legacyHeight != null;

  return (
    <div
      className={cn(
        'group relative cursor-pointer overflow-hidden',
        !usesLegacySize && sizeConfig.className,
        className
      )}
      style={usesLegacySize ? { width: qrSize, height: qrSize } : undefined}
    >
      <div
        ref={qrCodeRef}
        className="flex items-center justify-center transition-opacity group-hover:opacity-10"
      />
      {overlayType === 'download' && (
        <div
          className={overlayClasses}
          onClick={handleClick}
          onKeyUp={handleClick}
          role="button"
          tabIndex={0}
        >
          <UploadIcon size={32} className="text-foreground-muted" />
        </div>
      )}
      {overlayType === 'link' && (
        <Link className={overlayClasses} href={url} external tabIndex={0}>
          <ExternalLinkIcon size={32} className="text-foreground-muted" />
        </Link>
      )}
    </div>
  );
};

export default QrCodeGenerator;
